import { inject, Injectable } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
} from "@angular/router";
import { EMPTY, Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

import { Shop } from "@hermes/api-model-shop";
import { Context, LOCALE, REQUEST, RESPONSE } from "@hermes/core-app-core";
import { ShopsService } from "@hermes/domains-shop-fragments-shop-utils";
import { ShellFacade } from "@hermes/domains-shell-states-shell-state";
import { redirectResponse } from "@hermes/core-response-service";

import { SHELL_APPEARANCE_FULL } from "@hermes/utils-generic-constants";

import { RouterService } from "@hermes/utils-generic-services-router";

import { ShopAssetUrlPipe } from "../pipes/shop-asset-url.pipe";

@Injectable()
export class StorePageResolver {
    private readonly request = inject(REQUEST, { optional: true });
    private readonly response = inject(RESPONSE);
    private readonly locale = inject(LOCALE);
    private readonly shopsService = inject(ShopsService);
    private readonly shopAssetUrlPipe = inject(ShopAssetUrlPipe);
    private readonly routerService = inject(RouterService);
    private readonly context = inject(Context);
    private readonly router = inject(Router);
    private readonly shellFacade = inject(ShellFacade);

    /**
     * @returns Observable store-page api response
     */
    public resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<Shop> {
        this.shellFacade.setShell(SHELL_APPEARANCE_FULL);
        const [url, queryParameters] = state.url.slice(1).split("?");
        const shopId = (route.params.id as string).split("-").pop();

        if (!shopId) {
            this.redirectToNotFound(state);
            return EMPTY;
        }

        return this.shopsService.findShopById(shopId).pipe(
            tap((shop) => {
                if (url === shop.url) {
                    return;
                }

                const shopRedirectPath = `${shop.url}${
                    queryParameters ? `?${queryParameters}` : ""
                }`;

                if (this.context.isInServerMode() && this.request) {
                    redirectResponse({
                        request: this.request,
                        response: this.response,
                        newPath: `${this.locale.urlPrefix}/${shopRedirectPath}`,
                    });
                } else if (this.context.isInBrowserMode()) {
                    this.routerService.spaRedirect(`${shopRedirectPath}`);
                }
            }),
            map(
                (shop) =>
                    ({
                        ...shop,
                        assets: this.shopAssetUrlPipe.transform(shop.assets),
                    }) as Shop,
            ),
            catchError(() => {
                this.redirectToNotFound(state);
                return EMPTY;
            }),
        );
    }

    private redirectToNotFound(state: RouterStateSnapshot): void {
        if (this.context.isInServerMode()) {
            // on server rendering mode, send a 404 not found HTTP status
            this.response.status(404);
        }
        // redirect to 404-page without changing the url
        this.router.navigate(["/404-error"], {
            skipLocationChange: true,
            replaceUrl: false,
            queryParams: {
                originalUrl: state.url,
            },
        });
    }
}
